import { zodResolver } from "@hookform/resolvers/zod";
import { buildSavedViewMessage } from "application/src/app-utils/alert.tsx";
import {
  getDisabledPrjCapabilityTooltipMessage,
  prjPrivateSavingAllowed,
} from "application/src/app-utils/capability.ts";
import { useFormAria } from "application/src/app-utils/form/useFormAria.ts";
import { type Project } from "application/src/graphql.ts";
import { selMessage } from "application/src/messages/message.ts";
import { notify } from "application/src/modules/notifications/notify.tsx";
import { ProjectsSelect } from "application/src/modules/projects/components/ProjectsSelect.tsx";
import { ProjectTagMultiSelect } from "application/src/modules/projects/components/ProjectTagMultiSelect.tsx";
import type { ProjectBasisFragment } from "application/src/modules/projects/graphql/projects-queries.gql.ts";
import { getProjectLabel } from "application/src/redux/modules/config.ts";
import { CAPABILITY, MAX_LENGTH } from "application/src/settings/settings.ts";
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Callout,
  CalloutIntent,
  DialogComponent,
  Popover,
  Switch,
  TextField,
  TriggerButton,
  TriggerButtonSize,
  VStack,
} from "widgets";
import { useOverlay } from "widgets/hooks";
import { number, object, string } from "zod";

import { useGenomeViewerStore } from "../../store/store.ts";

const SaveViewSchema = object({
  name: string({ required_error: "Name is required" })
    .trim()
    .min(1, "Name is required")
    .max(MAX_LENGTH.VIEW.NAME, `Name limited to ${MAX_LENGTH.VIEW.NAME} characters`),
  description: string()
    .trim()
    .max(MAX_LENGTH.VIEW.DESCRIPTION, `Description limited to ${MAX_LENGTH.VIEW.DESCRIPTION} characters`)
    .optional(),
  projectId: number({ required_error: "Project is required" }),
});

export const SaveViewDialog = () => {
  const { overlayProps, state } = useOverlay();

  const privateFieldLabel = `Private (Hidden from other ${getProjectLabel(false, false)} members)`;
  const [selectedProject, setSelectedProject] = useState<ProjectBasisFragment>();
  const privateSavingAllowed = selectedProject && prjPrivateSavingAllowed(selectedProject as Project);
  const [updateView, setUpdateView] = useState<boolean>(false);
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tooltipTextForIsPrivate = useMemo(() => {
    if (selectedProject?.isPrivateWorkspace) {
      return "Items in the Private Workspace are never shared with other users.";
    } else if (!privateSavingAllowed) {
      return getDisabledPrjCapabilityTooltipMessage(CAPABILITY.PRIVATE_SAVING) || privateFieldLabel;
    }
    return privateFieldLabel;
  }, [privateFieldLabel, privateSavingAllowed, selectedProject?.isPrivateWorkspace]);

  const saveView = useGenomeViewerStore((state) => state.saveView);
  const syntenyId = useGenomeViewerStore((state) => state.syntenyOrganism.id);
  const viewMetadata = useGenomeViewerStore((state) => state.viewMetadata);
  const [submitting, setSubmitting] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useFormAria({
    resolver: zodResolver(SaveViewSchema),
  });

  const onSubmit = useCallback(async () => {
    if (!selectedProject) {
      return;
    }
    setSubmitting(true);
    const { name, description, projectTagIds, projectId, makePrivate } = getValues();
    const metadata = {
      id: updateView ? viewMetadata?.id : undefined,
      name,
      description,
      projectId: projectId,
      itemTagIds: projectTagIds ?? [],
      path: pathname.includes("/organism/") ? `/organism/${syntenyId}/synteny` : `/visualizer/synteny/${syntenyId}`,
      shared: !makePrivate,
    };
    saveView(metadata)
      .then((id) => {
        setSubmitting(false);
        state.close();
        notify.info({
          title: selMessage({ id: "view.save.successTitle" }),
          children: buildSavedViewMessage(name, selectedProject as Project, updateView && viewMetadata !== undefined),
        });
        // We get a new view ID, we need to update the URL.
        if (!updateView) {
          // `startTransition` is used to avoid blocking the main thread. Alternatively, we could use `setTimeout`.
          // Changing the search params in the main thread detaches the dialog from the DOM which makes it
          // completely unresponsive.
          startTransition(() => {
            searchParams.set("viewid", String(id));
            setSearchParams(searchParams, {
              replace: true,
            });
          });
        }
      })
      .catch((error) => {
        setSubmitting(false);
        notify.error({
          title: "Saving View Failed",
          children: `The view "${name}" could not be created in our database: ${error}`,
        });
      });
  }, [
    getValues,
    pathname,
    saveView,
    searchParams,
    selectedProject,
    setSearchParams,
    state,
    syntenyId,
    updateView,
    viewMetadata,
  ]);

  useEffect(() => {
    if (updateView && selectedProject && viewMetadata) {
      // This is a hack to ensure that the form resets `projectTagIds`
      // to the right value.
      setValue("projectTagIds", viewMetadata.itemTagIds);
    }
  }, [selectedProject, setValue, updateView, viewMetadata]);

  const resetToDefaults = useCallback(
    () => reset({ name: "", description: "", makePrivate: false, projectId: null, projectTagIds: [] }),
    [reset],
  );

  return (
    <>
      <TriggerButton
        size={TriggerButtonSize.Normal}
        icon={"bookmark_add"}
        onPress={() => {
          setUpdateView(false);
          setSelectedProject(undefined);
          // Note that I'm resetting the `projectId` to `null`.
          // The widget makes a difference between `undefined` and `null`.
          resetToDefaults();
          state.open();
        }}
        label={"Save"}
        showLabel
        ref={triggerRef}
      />
      <Popover state={state} {...overlayProps} triggerRef={triggerRef} placement="bottom end">
        <DialogComponent
          data-testid="save-view-dialog"
          mainButton={{
            label: "Save",
            onPress: () => handleSubmit(onSubmit)(),
            isDisabled: selectedProject === undefined,
            isLoading: submitting,
          }}
          closeFn={() => {
            // Only close the dialog if we are not submitting.
            if (!submitting) {
              state.close();
            }
          }}
          width={500}
          asPanelHeader
        >
          <form data-testid="save_view">
            <VStack spacing={15}>
              {viewMetadata?.id && (
                <Switch
                  isSelected={updateView}
                  onChange={(value) => {
                    setUpdateView(value);
                    if (value) {
                      reset({
                        name: viewMetadata.name,
                        description: viewMetadata.description ?? "",
                        makePrivate: !viewMetadata.shared,
                        projectId: viewMetadata.projectId,
                        projectTagIds: viewMetadata.itemTagIds,
                      });
                    } else {
                      // Note that I'm resetting the `projectId` to `null`.
                      // The widget makes a difference between `undefined` and `null`.
                      resetToDefaults();
                    }
                  }}
                  label="Update current view"
                />
              )}
              {viewMetadata?.id && updateView && (
                <Callout key="info" intent={CalloutIntent.Info} style={{ marginTop: "-5px" }}>
                  The previously saved view will be overwritten. Project and visibility are preserved.
                </Callout>
              )}
              <Controller
                control={control}
                name="projectId"
                render={({ field }) => (
                  <ProjectsSelect
                    label="Save to"
                    isRequired
                    tooltip="Select a destination"
                    onSelectionChange={(project: ProjectBasisFragment | undefined) => {
                      field.onChange(project?.id);
                      // If the project is not private, then make it public by default.
                      setValue("makePrivate", !!project?.isPrivateWorkspace);
                      setSelectedProject(project);
                    }}
                    isDisabled={submitting || updateView}
                    initialSelectedProjectId={field.value}
                    errorMessage={errors.projectId?.message?.toString()}
                    isInvalid={!!errors.projectId}
                  />
                )}
                defaultValue={undefined}
              />
              <Controller
                name="makePrivate"
                control={control}
                render={({ field }) => (
                  <Switch
                    isSelected={field.value}
                    onChange={field.onChange}
                    tooltip={tooltipTextForIsPrivate}
                    label={privateFieldLabel}
                    isDisabled={
                      submitting || updateView || selectedProject?.isPrivateWorkspace || !privateSavingAllowed
                    }
                  />
                )}
                defaultValue={true}
              />
              <Controller
                render={({ field }) => (
                  <TextField
                    label="Name"
                    isRequired
                    isInvalid={!!errors.name}
                    errorMessage={errors.name?.message?.toString()}
                    data-testid="name"
                    placeholder={"Enter view name"}
                    value={field.value}
                    onChange={field.onChange}
                    isDisabled={submitting}
                  />
                )}
                defaultValue=""
                name="name"
                control={control}
              />
              <Controller
                render={({ field }) => (
                  <TextField
                    label="Description"
                    data-testid="description"
                    rows={5}
                    value={field.value}
                    onChange={field.onChange}
                    isDisabled={submitting}
                  />
                )}
                defaultValue=""
                name="description"
                control={control}
              />
              <Controller
                name="projectTagIds"
                control={control}
                render={({ field }) => {
                  return (
                    <ProjectTagMultiSelect
                      label="Tags"
                      projectId={selectedProject?.id}
                      onSelectionChange={field.onChange}
                      selectedIds={field.value}
                      allowAddingTags
                      isDisabled={submitting}
                    />
                  );
                }}
                defaultValue={[]}
              />
            </VStack>
          </form>
        </DialogComponent>
      </Popover>
    </>
  );
};
